import React from "react";

// Same normalisation as the server (utils/pieceworkTagger.js → normalizeUnit)
export const normUnit = (raw) => {
  if (raw == null) return null;
  let s = String(raw)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  if (!s || s.length > 8 || !/\d/.test(s)) return null;
  if (/^\d+$/.test(s)) s = String(parseInt(s, 10));
  return s === "0" ? null : s;
};

const UNIT_PREFIX = /^(units?|suite|ste|apt|apartment)\s*#?\s*/i;

/**
 * Text search across notes, AI summary, module, tags and units.
 * "unit 6008", "#6008" and "6008" also match items that list 6008 in saved
 * units OR as a number inside the notes ("Unit 6007 vs 6008"), so it works on
 * records that were never tagged.
 */
export const matchesTextQuery = (item, raw) => {
  const q = String(raw || "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  const hay = [
    item.notes,
    item.aiSummary,
    item.moduleName,
    ...(item.tags || []),
    ...(item.units || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (hay.includes(q)) return true;

  const u = normUnit(q.replace(UNIT_PREFIX, "").replace(/^#/, ""));
  if (!u) return false;
  if ((item.units || []).some((x) => normUnit(x) === u)) return true;
  return hay.split(/[^a-z0-9]+/).some((w) => normUnit(w) === u);
};

export const buildTagOptions = (list, limit = 24) => {
  const m = new Map();
  (list || []).forEach((it) =>
    (it.tags || []).forEach((t) => m.set(t, (m.get(t) || 0) + 1)),
  );
  return [...m.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag))
    .slice(0, limit);
};

// AND semantics, same as the server's `tags: { $all }`
export const hasAllTags = (item, tags) =>
  !tags?.length || tags.every((t) => (item.tags || []).includes(t));

// ── Unit + tag pills ────────────────────────────────────────────────────────
export default function TagChips({
  units = [],
  tags = [],
  activeTags = [],
  onTagClick,
  onUnitClick,
  max = 6,
  className = "",
}) {
  if (!units.length && !tags.length) return null;
  const shown = units.slice(0, max);
  const extra = units.length - shown.length;
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {shown.map((u) => (
        <button
          key={`u-${u}`}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onUnitClick?.(u);
          }}
          title={`Search unit ${u}`}
          className="px-1.5 py-0.5 text-[10px] font-mono font-semibold rounded border bg-blue-900/30 border-blue-700/40 text-blue-300 hover:bg-blue-900/50 transition-colors"
        >
          {u}
        </button>
      ))}
      {extra > 0 && (
        <span className="text-[10px] text-gray-500 self-center">+{extra}</span>
      )}
      {tags.map((t) => {
        const on = activeTags.includes(t);
        return (
          <button
            key={`t-${t}`}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onTagClick?.(t);
            }}
            className={`px-1.5 py-0.5 text-[10px] rounded border transition-colors ${
              on
                ? "bg-orange-500/20 border-orange-500/50 text-orange-300"
                : "bg-gray-700/60 border-gray-600 text-gray-400 hover:text-gray-200"
            }`}
          >
            #{t}
          </button>
        );
      })}
    </div>
  );
}

// ── Possible-duplicate badge (requests table) ───────────────────────────────
export function DuplicateBadge({ duplicates = [], onClick }) {
  const open = duplicates.filter((d) => !d.acknowledged);
  if (!open.length) return null;
  const title = open
    .map(
      (d) =>
        `${d.requestNumber} (${d.status}) — unit ${d.sharedUnits.join(", ")}`,
    )
    .join("\n");
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      title={title}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded border bg-yellow-700/40 border-yellow-600/50 text-yellow-300 hover:bg-yellow-700/60 transition-colors"
    >
      ⚠ {open.length}
    </button>
  );
}
